import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { StaticKeyResponseType } from "../../../services/api/instances/staticTokenApi/types";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { AppTable } from "../../../shared/components/AppTable";
import { StaticTokenTableBody } from "./components/StaticTokenTableBody";
import { StaticTokenTableFooter } from "./components/StaticTokenTableFooter";
import { StaticTokenTableHead } from "./components/StaticTokenTableHead";
import {
  deleteStaticTokenAction,
  updateStaticTokenAction,
} from "./redux/actions";
import styles from "./styles.module.css";

interface Props {
  tokens: StaticKeyResponseType[];
  token: string;
  isPreview: boolean;
  onCreate: () => void;
}

export const StaticTokenCreatorTable = (props: Props) => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { t } = useTranslation();
  const prevTokensCount = useRef(props.tokens?.length);

  useEffect(() => {
    const currentTokensCount = props.tokens?.length;

    if (currentTokensCount > prevTokensCount.current) {
      const newPage = Math.floor((currentTokensCount - 1) / rowsPerPage);
      if (newPage !== page) {
        setPage(newPage);
      }
    } else if (currentTokensCount < prevTokensCount.current) {
      const itemsOnCurrentPage = Math.min(
        rowsPerPage,
        currentTokensCount - page * rowsPerPage
      );

      if (itemsOnCurrentPage === 0 && page > 0) {
        setPage(page - 1);
      }
    }

    prevTokensCount.current = currentTokensCount;
  }, [props.tokens?.length, rowsPerPage, page]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleDeleteStaticToken = (id: number) => {
    dispatch(deleteStaticTokenAction(id));
  };

  const handleDateChange = (date: Date, id: number) => {
    const postObj = {
      static_key: props.tokens?.find((token) => token?.id === id)
        ?.static_key as string,
      valid_till: date,
    };
    dispatch(updateStaticTokenAction({ postObj: postObj, id: id }));
  };

  return (
    <AppTable>
      <StaticTokenTableHead className={props.isPreview ? styles.opacity : ""} />
      <StaticTokenTableBody
        token={props.token}
        isPreview={props.isPreview}
        onCreate={props.onCreate}
        onChange={handleDateChange}
        onDelete={handleDeleteStaticToken}
        tokens={props.tokens?.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )}
      />
      {props.tokens?.length > 5 && (
        <StaticTokenTableFooter
          count={props.tokens?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${t("versioning.table_pagination_separator_preposition")} ${count !== -1 ? count : `${to}`}`
          }
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className={props.isPreview ? styles.opacity : ""}
        />
      )}
    </AppTable>
  );
};
